import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../constants';
import { reportShareUrl } from './PublishReportControls';

const apiBase = `${API_URL}/sweden-snus-admin`;

function reportLabel(report) {
  const scope = report.scope || report.retailer_label || report.country || 'Report';
  const week = report.week_label || report.week || '';
  return week ? `${scope} · ${week}` : scope;
}

export default function SwedenSnusReportEmbedView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestedHash = searchParams.get('hash') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${apiBase}/reports`, { withCredentials: true });
        if (!cancelled) setReports(res.data?.reports || []);
      } catch (e) {
        if (!cancelled) {
          setReports([]);
          setError(e.response?.data?.detail || e.message || 'Failed to load published reports');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(() => {
    if (!reports.length) return null;
    return reports.find((report) => report.hash === requestedHash) || reports[0];
  }, [reports, requestedHash]);

  useEffect(() => {
    if (!selected?.hash || selected.hash === requestedHash) return;
    setSearchParams({ hash: selected.hash }, { replace: true });
  }, [selected, requestedHash, setSearchParams]);

  const pageUrl = selected?.hash ? `${API_URL}/sweden-snus-admin/r/${encodeURIComponent(selected.hash)}` : '';
  const shareUrl = selected?.hash ? reportShareUrl(selected.hash) : '';

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          p: 1.5,
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 320, maxWidth: 560, flex: 1 }}>
          <InputLabel id="shelf-report-label">Report</InputLabel>
          <Select
            labelId="shelf-report-label"
            label="Report"
            value={selected?.hash || ''}
            onChange={(event) => setSearchParams({ hash: event.target.value })}
            disabled={loading || reports.length === 0}
          >
            {reports.map((report) => (
              <MenuItem key={report.hash} value={report.hash}>
                {reportLabel(report)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {shareUrl && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Report page is also available outside Insights at{' '}
            <Link href={shareUrl} target="_blank" rel="noopener noreferrer">
              {shareUrl}
            </Link>
          </Typography>
        )}
      </Paper>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2, alignSelf: 'flex-start' }}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        )}
        {!error && !loading && reports.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            No published reports yet. Open Data & shelf health and use Publish page on a week.
          </Typography>
        )}
        {pageUrl && (
          <iframe
            title="Shelf report"
            src={pageUrl}
            style={{ flex: 1, width: '100%', border: 'none', display: 'block', minHeight: 0 }}
          />
        )}
      </Box>
    </Box>
  );
}
